import { useState } from 'react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, startOfDay, endOfDay } from 'date-fns';
import api from '../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from './LoadingSpinner';

export default function KPICalculator() {
  const [loading, setLoading] = useState(false);
  const [periodType, setPeriodType] = useState('monthly');
  const [periodStart, setPeriodStart] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [periodEnd, setPeriodEnd] = useState(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
  const [result, setResult] = useState(null);

  const handlePeriodTypeChange = (type) => {
    setPeriodType(type);
    const now = new Date();

    switch (type) {
      case 'daily':
        setPeriodStart(format(startOfDay(now), 'yyyy-MM-dd'));
        setPeriodEnd(format(endOfDay(now), 'yyyy-MM-dd'));
        break;
      case 'weekly':
        setPeriodStart(format(startOfWeek(now), 'yyyy-MM-dd'));
        setPeriodEnd(format(endOfWeek(now), 'yyyy-MM-dd'));
        break;
      case 'monthly':
        setPeriodStart(format(startOfMonth(now), 'yyyy-MM-dd'));
        setPeriodEnd(format(endOfMonth(now), 'yyyy-MM-dd'));
        break;
    }
  };

  const handleCalculate = async () => {
    try {
      setLoading(true);
      setResult(null);

      const response = await api.post('/kpis/calculate-all', {
        period_type: periodType,
        period_start: periodStart,
        period_end: periodEnd,
      });

      setResult(response.data.data);

      if (response.data.data.errors.length > 0) {
        toast.success(
          `Calculated KPIs for ${response.data.data.calculated} drivers with ${response.data.data.errors.length} errors`,
          { duration: 5000 }
        );
      } else {
        toast.success(`Successfully calculated KPIs for ${response.data.data.calculated} drivers!`);
      }
    } catch (error) {
      console.error('Failed to calculate KPIs:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to calculate KPIs');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Calculate KPIs</h2>
          <p className="text-sm text-gray-600">Calculate performance metrics for all drivers</p>
        </div>
        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        </div>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Period Type
            </label>
            <select
              value={periodType}
              onChange={(e) => handlePeriodTypeChange(e.target.value)}
              className="w-full px-3 py-2 border rounded-md"
              disabled={loading}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
              className="w-full px-3 py-2 border rounded-md"
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
              className="w-full px-3 py-2 border rounded-md"
              disabled={loading}
            />
          </div>
        </div>

        <button
          onClick={handleCalculate}
          disabled={loading}
          className="btn btn-primary w-full"
        >
          {loading ? (
            <span className="flex items-center justify-center">
              <LoadingSpinner size="sm" className="mr-2" />
              Calculating...
            </span>
          ) : (
            'Calculate KPIs for All Drivers'
          )}
        </button>

        {result && (
          <div className="mt-4 p-4 bg-gray-50 rounded-md space-y-2">
            <h3 className="font-medium text-gray-900">Calculation Results</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-gray-600">Total Drivers</div>
                <div className="text-xl font-semibold text-gray-900">{result.total}</div>
              </div>
              <div>
                <div className="text-gray-600">Calculated</div>
                <div className="text-xl font-semibold text-green-600">{result.calculated}</div>
              </div>
              <div>
                <div className="text-gray-600">Created</div>
                <div className="text-xl font-semibold text-blue-600">{result.created}</div>
              </div>
              <div>
                <div className="text-gray-600">Updated</div>
                <div className="text-xl font-semibold text-yellow-600">{result.updated}</div>
              </div>
            </div>

            {result.errors.length > 0 && (
              <div className="mt-4">
                <h4 className="font-medium text-red-900 mb-2">Errors ({result.errors.length})</h4>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {result.errors.map((error, index) => (
                    <div key={index} className="text-sm text-red-700 bg-red-50 p-2 rounded">
                      <strong>{error.driver_name}:</strong> {error.error}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-800">
          <strong>Note:</strong> KPIs are calculated from approved log entries only. This process will
          create or update KPI records for all active drivers for the selected period.
        </div>
      </div>
    </div>
  );
}
